/**
 * Business Rules Service
 * Service for fetching combined system + tenant rules for the Business Rules grid
 */
import { apiClient } from '@/lib/apiClient';
import type { AbpPagedResponse } from '@/lib/gridDataSourceUtils';

/**
 * Unified Rule DTO for grid display (combines system and tenant rules)
 */
export interface UnifiedRule {
  id: string;
  ruleNumber: string;
  ruleKey: string;
  name: string;
  description?: string;
  module: string;
  entity: string;
  orgUnitId?: string;
  orgUnitName?: string;
  /** Source: "System", "Tenant", or "OrgUnit" */
  source: 'System' | 'Tenant' | 'OrgUnit';
  enabled: boolean;
  priority: number;
  version: number;
  canBeOverridden?: boolean;
  isOverride?: boolean;
  status?: string;
  errorMessage?: string;
  errorType?: string;
  trigger?: string;
  when?: string;
  expression?: string;
  actions?: string;
  /** C1: "fail" (default) or "skip" when a relation the rule reads is null. */
  nullRelationBehavior?: string;
  /** C2: "fail" (default) or "skip" when a number/boolean/date operand is null. */
  nullFieldBehavior?: string;
  /** "rulesEngine" (engine-evaluated) or "domain" (enforced by a C# site). */
  executionStrategy?: string;
  /** State-machine transitions the rule is mapped to (engine rules run on these). */
  transitions?: { key: string; from: string; to: string; description?: string }[];
  creationTime: string;
  /** Resolved user display name; null for seeded system rules */
  creatorName?: string;
  lastModificationTime?: string;
  /** Resolved user display name; null for seeded system rules */
  lastModifierName?: string;
  /** True for system rules (cannot be edited, only overridden) */
  isReadOnly: boolean;
  /** True if this system rule has been disabled for the current tenant */
  isDisabledForTenant?: boolean;
}

export interface AllRulesResponse {
  items: UnifiedRule[];
  totalCount: number;
}

export interface AllRulesParams {
  module?: string;
  entity?: string;
}

export class BusinessRulesService {
  // Uses ABP conventional routing: IBusinessRuleAppService.GetCombinedListAsync
  private readonly baseUrl = '/api/core/businessrule';

  /**
   * Get all rules (system + tenant + orgUnit) for the Business Rules grid
   * Uses the conventional endpoint generated by ABP
   */
  async getAllRules(params?: AllRulesParams): Promise<AllRulesResponse> {
    const response = await apiClient.get<AllRulesResponse>(`${this.baseUrl}/combined-list`, {
      params,
    });
    return response.data;
  }

  /**
   * Get all rules formatted for DataGrid (compatible with AbpPagedResponse)
   */
  async getListForGrid(params?: AllRulesParams): Promise<AbpPagedResponse<UnifiedRule>> {
    const response = await this.getAllRules(params);
    return {
      items: response.items,
      totalCount: response.totalCount,
    };
  }

  /**
   * Disable a system rule for the current tenant
   */
  async disableSystemRule(systemRuleId: string): Promise<void> {
    await apiClient.post(`/api/core/system-rule-disable/disable/${systemRuleId}`);
  }

  /**
   * Enable a previously disabled system rule for the current tenant
   */
  async enableSystemRule(systemRuleId: string): Promise<void> {
    await apiClient.post(`/api/core/system-rule-disable/enable/${systemRuleId}`);
  }

  /**
   * Check if a system rule is disabled for the current tenant
   */
  async isSystemRuleDisabled(systemRuleId: string): Promise<boolean> {
    const response = await apiClient.get<boolean>(`/api/core/system-rule-disable/is-disabled/${systemRuleId}`);
    return response.data;
  }
}

// Export singleton instance
export const businessRulesService = new BusinessRulesService();
